import { useEffect, useState } from "react";
import { api, fmtMinutes } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Activity, ClipboardList, CheckCircle2, TriangleAlert, Timer,
  TrendingUp, Wrench, Zap,
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
  PieChart, Pie, Cell,
} from "recharts";

const CHART_COLORS = ["#F97316", "#3B82F6", "#EAB308", "#16A34A", "#DC2626"];

function KPI({ icon: Icon, label, value, hint, tone = "default", testId }) {
  const tones = {
    default: "border-slate-200 dark:border-slate-800",
    danger: "border-red-500/30",
    warn: "border-yellow-500/30",
    ok: "border-green-500/30",
  };
  return (
    <Card data-testid={testId} className={`rounded-sm shadow-none border p-5 ${tones[tone]}`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-muted-foreground">{label}</div>
          <div className="font-heading font-black text-3xl mt-2">{value}</div>
          {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
        </div>
        <div className="w-9 h-9 rounded-sm bg-secondary flex items-center justify-center">
          <Icon className="w-4 h-4 text-orange-500" />
        </div>
      </div>
    </Card>
  );
}

export default function Dashboard() {
  const [kpis, setKpis] = useState(null);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    api.get("/dashboard/kpis").then((r) => setKpis(r.data));
    api.get("/os").then((r) => setRecent(r.data.slice(0, 5)));
  }, []);

  if (!kpis) return <div className="text-muted-foreground">Carregando...</div>;

  return (
    <div className="space-y-6" data-testid="dashboard">
      <div>
        <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-muted-foreground">Painel Operacional</div>
        <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tight">Induscalta Morro Azul — PCM</h1>
        <p className="text-muted-foreground mt-1">Visão geral de manutenção — Britagem & Moagem</p>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPI icon={ClipboardList} label="Total de OS" value={kpis.total} testId="kpi-total" />
        <KPI icon={TriangleAlert} label="Abertas" value={kpis.open} tone="danger" testId="kpi-open" />
        <KPI icon={Activity} label="Em Andamento" value={kpis.in_progress} tone="warn" testId="kpi-progress" />
        <KPI icon={CheckCircle2} label="Concluídas" value={kpis.done} tone="ok" testId="kpi-done" />
        <KPI icon={Timer} label="MTTR" value={fmtMinutes(kpis.mttr_minutes)} hint="Tempo médio de reparo" testId="kpi-mttr" />
        <KPI icon={TrendingUp} label="MTBF" value={fmtMinutes(kpis.mtbf_minutes)} hint="Tempo médio entre falhas" testId="kpi-mtbf" />
        <KPI icon={Zap} label="Tempo Parado Total" value={fmtMinutes(kpis.avg_downtime_minutes)} hint="Média por OS com parada" testId="kpi-downtime" />
        <KPI icon={Wrench} label="Canceladas" value={kpis.cancelled} testId="kpi-cancelled" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPI icon={Wrench} label="Equipamentos" value={kpis.equipment_count} hint="Cadastrados" testId="kpi-equipment-count" />
        <KPI icon={Activity} label="Setores" value={kpis.sector_count} hint="Cadastrados" testId="kpi-sector-count" />
        <KPI icon={ClipboardList} label="Disciplinas" value={kpis.discipline_count} hint="Cadastradas" testId="kpi-discipline-count" />
        <KPI icon={TriangleAlert} label="Estoque Baixo" value={kpis.low_stock_count} tone={kpis.low_stock_count > 0 ? "danger" : "default"} hint="Peças abaixo do mínimo" testId="kpi-low-stock" />
      </div>

      {/* Charts */}
      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2 rounded-sm shadow-none p-5">
          <div className="mb-4">
            <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-muted-foreground">Tendência 7 dias</div>
            <h3 className="font-heading text-xl font-bold">Aberturas de OS</h3>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={kpis.trend}>
              <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.2} />
              <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} fontSize={11} />
              <YAxis fontSize={11} allowDecimals={false} />
              <Tooltip contentStyle={{ backgroundColor: "#0F172A", border: "none", color: "#fff", borderRadius: 4 }} />
              <Bar dataKey="count" fill="#F97316" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card className="rounded-sm shadow-none p-5">
          <div className="mb-4">
            <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-muted-foreground">Distribuição</div>
            <h3 className="font-heading text-xl font-bold">Por Manutenção</h3>
          </div>
          {(kpis.by_maintenance_type || []).length === 0 ? (
            <div className="text-sm text-muted-foreground py-8 text-center">Sem dados ainda</div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={kpis.by_maintenance_type} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label>
                  {kpis.by_maintenance_type.map((entry) => <Cell key={entry.name} fill={CHART_COLORS[kpis.by_maintenance_type.indexOf(entry) % CHART_COLORS.length]} />)}
                </Pie>
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Tooltip contentStyle={{ backgroundColor: "#0F172A", border: "none", color: "#fff", borderRadius: 4 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Card>
      </div>

      {/* Recent OS */}
      <Card className="rounded-sm shadow-none p-5">
        <div className="mb-4 flex items-baseline justify-between">
          <div>
            <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-muted-foreground">Últimas atividades</div>
            <h3 className="font-heading text-xl font-bold">OS Recentes</h3>
          </div>
        </div>
        {recent.length === 0 ? (
          <div className="text-sm text-muted-foreground py-8 text-center">Nenhuma OS registrada ainda. Clique em "Nova OS" para começar.</div>
        ) : (
          <div className="space-y-2">
            {recent.map((o) => (
              <div key={o.id} className="flex items-center justify-between border rounded-sm p-3 hover:border-orange-500 transition-colors">
                <div className="flex items-center gap-4">
                  <div className="font-mono-tag text-sm font-semibold">OS #{o.number}</div>
                  <div>
                    <div className="font-medium text-sm">{o.equipment_name} <span className="font-mono-tag text-xs text-muted-foreground">[{o.equipment_tag}]</span></div>
                    <div className="text-xs text-muted-foreground line-clamp-1">{o.description}</div>
                  </div>
                </div>
                <Badge variant="outline" className="rounded-sm">{o.status}</Badge>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
